import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { StaffSpeciality } from '@/types/settings-api';

/**
 * SNOMED speciality typeahead — powers the SPECIALITY chips field on the
 * Add Practitioner sub-wizard (PR 4) AND the practitioner Account tab
 * stub at `practitioner-account-tab.tsx:233`.
 *
 *   GET /api/Master/PracticeCodeList?searchQuery={q}&system={s}
 *
 * `system` is the SNOMED URI `http://snomed.info/sct` (matches the
 * `code.system` field stored on `doctorSpecialities[]`). Captured against
 * Flutter `common_data_source_impl.dart:379-395` (`practiceCodeList`)
 * with the value-set model `speciality_valueset_list_model.dart`.
 *
 * Response shape mirrors the persisted `StaffSpeciality` envelope —
 * `{specialitySkillId, code: {code, displayName, system, description, shortName}}`.
 * The `specialitySkillId` is a stable backend GUID per master record, so
 * it round-trips through the create body unchanged.
 *
 * Empty / 1-char queries yield no fetch — the hook gates on
 * `trimmed.length >= MIN_QUERY_LEN` (default 2) so single-character
 * keystrokes don't hammer the endpoint while the user is typing
 * (Samer review PR #323 — debounce is necessary but not sufficient).
 * Callers should ALSO debounce input on top (e.g. `useDebounce(query, 300)`).
 */
export const SNOMED_SYSTEM = 'http://snomed.info/sct';

/**
 * Minimum query length to allow the typeahead fetch. 2 characters is
 * the cheapest gate that still returns useful matches (single-letter
 * SNOMED queries return ~thousands of rows that aren't UX-useful).
 * Callers can override per-instance if a UX reason demands it.
 */
export const MIN_QUERY_LEN = 2;

export function useSpecialityCodeList(query: string, minLen: number = MIN_QUERY_LEN) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['settings', 'speciality-code-list', trimmed],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({
        searchQuery: trimmed,
        system: SNOMED_SYSTEM,
      });
      return get<StaffSpeciality[]>(
        `/api/Master/PracticeCodeList?${qs.toString()}`,
        { signal },
      );
    },
    enabled: trimmed.length >= minLen,
    placeholderData: keepPreviousData,
    staleTime: 60 * 1000,
  });
}
